import {
	notificationIndexQueryKey,
	notificationUnreadCountQueryKey,
	useNotificationIndex,
	useNotificationMarkAllAsRead,
	useNotificationUnreadCount,
} from '@jobilla/nexus-sdk'
import type { NotificationData, NotificationPayloadData, NotificationUnreadCountResponse } from '@jobilla/nexus-sdk'
// eslint-disable-next-line import/no-internal-modules
import { useJobillaOrgId } from '@/hooks'
import { useAuth } from '@/lib'
import { logger } from '@/ui'
import { Z_INDEX_POPUP } from '@/ui/rapid-ui/constants'
import { useWhatsAppEnabled } from '@/ui/utilities/hooks/use-whatsapp-enabled'
import { notificationMarkAsRead } from '@jobilla/nexus-sdk/lib/axios'
import { type QueryClient, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import {
	AlertTriangleIcon,
	AtSignIcon,
	BellIcon,
	BotIcon,
	BriefcaseIcon,
	CalendarCheckIcon,
	CalendarXIcon,
	CheckIcon,
	MailIcon,
	MessageCircleIcon,
} from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { notificationRoute } from './notification-route'
import { NotificationSettingsButton } from './notification-settings-button'

const UNREAD_COUNT_KEY = notificationUnreadCountQueryKey()
const INDEX_KEY = notificationIndexQueryKey()

type ReadSnapshot = {
	prevCount: NotificationUnreadCountResponse | undefined
	prevIndex: NotificationData[] | undefined
}

async function applyReadOptimistically(
	queryClient: QueryClient,
	updates: {
		count: (old: NotificationUnreadCountResponse | undefined) => NotificationUnreadCountResponse
		index: (old: NotificationData[] | undefined) => NotificationData[] | undefined
	},
): Promise<ReadSnapshot> {
	await Promise.all([
		queryClient.cancelQueries({ queryKey: UNREAD_COUNT_KEY }),
		queryClient.cancelQueries({ queryKey: INDEX_KEY }),
	])

	const prevCount = queryClient.getQueryData<NotificationUnreadCountResponse>(UNREAD_COUNT_KEY)
	const prevIndex = queryClient.getQueryData<NotificationData[]>(INDEX_KEY)

	queryClient.setQueryData<NotificationUnreadCountResponse>(UNREAD_COUNT_KEY, updates.count)
	queryClient.setQueryData<NotificationData[]>(INDEX_KEY, updates.index)

	return { prevCount, prevIndex }
}

export function NotificationBell() {
	const { t } = useTranslation('common')
	const organisationId = useJobillaOrgId()
	const { getAccessToken } = useAuth()

	const [isOpen, setIsOpen] = useState(false)
	const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 })
	const buttonRef = useRef<HTMLButtonElement>(null)
	const dropdownRef = useRef<HTMLDivElement>(null)
	const queryClient = useQueryClient()
	const navigate = useNavigate()
	const isWhatsappEnabled = useWhatsAppEnabled()

	// Interview notifications carry structured fields (candidateName + type) so the
	// label renders in the recruiter's current UI locale; everything else falls back
	// to the server-built `message` string.
	const getNotificationMessage = (data: NotificationPayloadData): string => {
		const candidateName = data.candidateName ?? t('notifications.aCandidate')

		switch (data.type) {
			case 'interview_booked':
				return t('notifications.interviewBooked', { candidateName })
			case 'interview_cancelled':
				return t('notifications.interviewCancelled', { candidateName })
			case 'interview_stage_move_failed':
				return t('notifications.interviewStageMoveFailed', { candidateName })
			case 'calendar_connection_broken':
				return t('notifications.calendarConnectionBroken')
			case 'campaign_closed_with_ats_job': {
				const campaignName = data.campaignName ?? t('notifications.aCampaign')
				return data.status === 'withdrawn'
					? t('notifications.campaignWithdrawnAtsJobClosed', { campaignName })
					: t('notifications.campaignStoppingAtsJobClosed', { campaignName })
			}
			case 'candidate_note_mention':
				return t('notifications.noteMention', {
					authorName: data.authorName ?? t('notes.unknownUser'),
					candidateName,
				})
			case 'whatsapp_ai_conversation_ended':
				if (data.status === 'handed_off') {
					return t('notifications.aiInterviewNeedsHuman', { candidateName })
				}
				if (data.reason === 'off_topic') {
					return t('notifications.aiInterviewEndedOffTopic', { candidateName })
				}
				if (data.reason === 'declined') {
					return t('notifications.aiInterviewDeclined', { candidateName })
				}
				if (data.reason === 'ai_consent_missing') {
					return t('notifications.aiInterviewStoppedNoConsent', { candidateName })
				}
				return t('notifications.aiInterviewFinished', { candidateName })
			default:
				return data.message ?? t('notifications.newNotification')
		}
	}

	const { data: countData } = useNotificationUnreadCount({
		enabled: Boolean(organisationId),
		refetchInterval: 30000,
	})

	const { data: notificationsData } = useNotificationIndex({
		enabled: Boolean(organisationId) && (isOpen || isWhatsappEnabled),
		refetchInterval: isWhatsappEnabled ? 30000 : undefined,
	})

	const markAllAsReadMutation = useNotificationMarkAllAsRead({
		onMutate: () =>
			applyReadOptimistically(queryClient, {
				count: () => ({ count: 0 }),
				index: (old) => {
					if (!old) { return old }
					const now = new Date().toISOString()
					return old.map((n) => n.readAt ? n : { ...n, readAt: now })
				},
			}),
		onError: (err, _vars, ctx) => {
			logger.error(err, {
				tags: { component: 'NotificationBell', action: 'markAllAsRead' },
			})
			const snapshot = ctx as ReadSnapshot | undefined
			if (!snapshot) { return }
			queryClient.setQueryData(UNREAD_COUNT_KEY, snapshot.prevCount)
			queryClient.setQueryData(INDEX_KEY, snapshot.prevIndex)
		},
		onSettled: () => {
			void queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY })
			void queryClient.invalidateQueries({ queryKey: INDEX_KEY })
		},
	})

	const isMalformedPayload = notificationsData !== undefined && !Array.isArray(notificationsData)

	useEffect(() => {
		if (!isMalformedPayload) { return }
		logger.warn('GET /notifications returned a non-array body', {
			category: 'api',
			tags: { component: 'NotificationBell' },
			data: { receivedType: typeof notificationsData },
		})
	}, [isMalformedPayload, notificationsData])

	const allNotifications = useMemo(
		() => Array.isArray(notificationsData) ? notificationsData : [],
		[notificationsData],
	)

	const notifications = useMemo(
		() =>
			isWhatsappEnabled
				? allNotifications
				: allNotifications.filter((notification) => notification.data.type !== 'whatsapp_reply'),
		[allNotifications, isWhatsappEnabled],
	)

	const unreadCount = useMemo(() => {
		const total = Number(countData?.count ?? 0)
		if (isWhatsappEnabled) { return total }
		const whatsappUnread = allNotifications.filter(
			(notification) => notification.data.type === 'whatsapp_reply' && !notification.readAt,
		).length
		return Math.max(0, total - whatsappUnread)
	}, [countData?.count, allNotifications, isWhatsappEnabled])

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as Node
			if (
				dropdownRef.current && !dropdownRef.current.contains(target)
				&& buttonRef.current && !buttonRef.current.contains(target)
			) {
				setIsOpen(false)
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	const handleToggle = useCallback(() => {
		setIsOpen((prev) => {
			if (!prev && buttonRef.current) {
				const rect = buttonRef.current.getBoundingClientRect()
				setDropdownPosition({
					top: rect.bottom + 8,
					right: window.innerWidth - rect.right,
				})
				logger.track('Notifications: Opened', {
					data: { unread_count: unreadCount },
				})
			}
			return !prev
		})
	}, [unreadCount])

	const handleMarkAsRead = useCallback(async (notificationId: string) => {
		const snapshot = await applyReadOptimistically(queryClient, {
			count: (old) => {
				const current = old?.count ?? 0
				return { count: Math.max(0, current - 1) }
			},
			index: (old) => {
				if (!old) { return old }
				const now = new Date().toISOString()
				return old.map((n) => n.id === notificationId && !n.readAt ? { ...n, readAt: now } : n)
			},
		})

		try {
			const token = await getAccessToken()
			await notificationMarkAsRead(notificationId, {
				baseURL: import.meta.env.VITE_NEXUS_API_URL as string,
				headers: {
					Authorization: token ? `Bearer ${token}` : '',
				},
			})
		} catch (error) {
			queryClient.setQueryData(UNREAD_COUNT_KEY, snapshot.prevCount)
			queryClient.setQueryData(INDEX_KEY, snapshot.prevIndex)
			logger.error(error, {
				tags: { component: 'NotificationBell' },
				data: { notificationId },
			})
			return
		}

		void queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY })
		void queryClient.invalidateQueries({ queryKey: INDEX_KEY })
	}, [getAccessToken, queryClient])

	const handleRowClick = useCallback(
		(notification: NotificationData) => {
			const route = notificationRoute(notification)

			if (route) {
				navigate(route)
				setIsOpen(false)
			} else {
				logger.warn('notification-bell: no route mapping for notification', {
					data: {
						id: notification.id,
						type: notification.type,
						discriminator: notification.data.type,
					},
				})
			}

			if (!notification.readAt) {
				void handleMarkAsRead(notification.id)
			}
		},
		[navigate, handleMarkAsRead],
	)

	const handleMarkAllAsRead = useCallback(() => {
		markAllAsReadMutation.mutate(undefined as never)
	}, [markAllAsReadMutation])

	const getNotificationIcon = (type: string | undefined) => {
		switch (type) {
			case 'whatsapp_reply':
				return <MessageCircleIcon className="size-4 text-green-600" />
			case 'whatsapp_template_status':
			case 'whatsapp_inactivity':
				return <MessageCircleIcon className="size-4 text-purple-600" />
			case 'candidate_reply':
				return <MailIcon className="size-4 text-blue-600" />
			case 'interview_booked':
				return <CalendarCheckIcon className="size-4 text-green-600" />
			case 'interview_cancelled':
				return <CalendarXIcon className="size-4 text-amber-600" />
			case 'interview_stage_move_failed':
				return <AlertTriangleIcon className="size-4 text-red-600" />
			case 'calendar_connection_broken':
				return <CalendarXIcon className="size-4 text-red-600" />
			case 'campaign_closed_with_ats_job':
				return <BriefcaseIcon className="size-4 text-amber-600" />
			case 'candidate_note_mention':
				return <AtSignIcon className="size-4 text-brand-600" />
			case 'whatsapp_ai_conversation_ended':
				return <BotIcon className="size-4 text-purple-600" />
			default:
				return <BellIcon className="size-4 text-gray-600" />
		}
	}

	const formatTime = (dateString: string) => {
		const date = new Date(dateString)
		if (isNaN(date.getTime())) { return '' }

		const now = new Date()
		const diffMs = now.getTime() - date.getTime()
		const diffMins = Math.floor(diffMs / 60000)
		const diffHours = Math.floor(diffMs / 3600000)
		const diffDays = Math.floor(diffMs / 86400000)

		if (diffMins < 1) { return t('notifications.justNow') }
		if (diffMins < 60) { return t('notifications.minutesAgo', { count: diffMins }) }
		if (diffHours < 24) { return t('notifications.hoursAgo', { count: diffHours }) }
		if (diffDays < 7) { return t('notifications.daysAgo', { count: diffDays }) }
		return date.toLocaleDateString()
	}

	const dropdown = (
		<AnimatePresence>
			{isOpen && (
				<motion.div
					ref={dropdownRef}
					initial={{ opacity: 0, y: -10 }}
					animate={{ opacity: 1, y: 0 }}
					exit={{ opacity: 0, y: -10 }}
					style={{ top: dropdownPosition.top, right: dropdownPosition.right, zIndex: Z_INDEX_POPUP }}
					className="fixed w-80 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
				>
					<div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
						<h3 className="font-medium text-gray-900">{t('notifications.title')}</h3>
						<div className="flex items-center gap-2">
							{unreadCount > 0 && (
								<button
									type="button"
									onClick={handleMarkAllAsRead}
									className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1 cursor-pointer"
								>
									<CheckIcon className="size-3" />
									{t('notifications.markAllRead')}
								</button>
							)}
							<NotificationSettingsButton />
						</div>
					</div>

					<div className="max-h-96 overflow-y-auto">
						{notifications.length === 0
							? (
								<div className="p-8 text-center text-gray-500">
									<BellIcon className="size-8 mx-auto mb-2 text-gray-300" />
									<p className="text-sm">{t('notifications.empty')}</p>
								</div>
							)
							: (
								<div className="divide-y divide-gray-100">
									{notifications.map((notification) => (
										<button
											key={notification.id}
											type="button"
											onClick={() => handleRowClick(notification)}
											className={`w-full text-left p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
												!notification.readAt ? 'bg-purple-50/50' : ''
											}`}
										>
											<div className="flex items-start gap-3">
												<div className="mt-0.5">
													{getNotificationIcon(notification.data.type)}
												</div>
												<div className="flex-1 min-w-0">
													<p
														className={`text-sm ${
															!notification.readAt
																? 'font-medium text-gray-900'
																: 'text-gray-700'
														}`}
													>
														{getNotificationMessage(notification.data)}
													</p>
													{notification.data.preview && (
														<p className="text-xs text-gray-500 mt-1 truncate">
															{notification.data.preview}
														</p>
													)}
													{notification.data.campaignName && (
														<p
															data-testid={`notification-campaign-label-${notification.id}`}
															className="text-xs text-gray-500 mt-1 truncate"
														>
															{notification.data.campaignName}
														</p>
													)}
													<p className="text-xs text-gray-400 mt-1">
														{formatTime(notification.createdAt)}
													</p>
												</div>
												{!notification.readAt && (
													<div className="size-2 bg-purple-500 rounded-full mt-2" />
												)}
											</div>
										</button>
									))}
								</div>
							)}
					</div>
				</motion.div>
			)}
		</AnimatePresence>
	)

	return (
		<>
			<button
				ref={buttonRef}
				type="button"
				onClick={handleToggle}
				className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
				aria-label={t('notifications.title')}
			>
				<BellIcon className="size-5" />
				{unreadCount > 0 && (
					<span className="absolute top-0 right-0 size-5 bg-red-500 text-white text-xs font-medium rounded-full flex items-center justify-center">
						{unreadCount > 9 ? '9+' : unreadCount}
					</span>
				)}
			</button>
			{createPortal(dropdown, document.body)}
		</>
	)
}
